import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatError } from "@/api/client";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("oursubs@handletech.io");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const login = useAuthStore((s) => s.login);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      const target = location.state?.from || "/dashboard";
      navigate(target);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 group">
          <div className="w-10 h-10 bg-[#41B5E6] flex items-center justify-center rounded-sm">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-black text-lg tracking-tight">Handle Docs</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Documentation Platform</div>
          </div>
        </Link>

        <div className="bg-white border border-zinc-200 p-8 rounded-sm">
          <h1 className="text-2xl font-bold mb-1">Sign in</h1>
          <p className="text-sm text-zinc-500 mb-6">Welcome back. Please enter your credentials.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6] focus:border-transparent"
              />
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <label className="block text-sm font-semibold">Password</label>
                <Link to="/forgot-password" className="text-xs text-[#41B5E6] hover:underline">
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#41B5E6] focus:border-transparent"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold py-2.5 rounded-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>

          <p className="text-sm text-center mt-6 text-zinc-600">
            New to Handle Docs?{" "}
            <Link to="/register" className="text-[#41B5E6] font-semibold hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
